import { useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { Notes } from "../../orders/utils/types";
import { useCustomMutation, useInvalidate } from "@refinedev/core";
import { useMarketplaceStore } from "../../../store/useMarketplaceStore";
import { useParams } from "react-router-dom";
import { useTranslation } from "../../../hooks/useTranslation";
import { OrderMessagesCard } from "./OrderMessagesCard";

/* =========================
   TYPES
========================= */

interface OrderNotesProps {
  orderNotes: Notes[];
}

type NotesResponse = {
  notes?: Notes[];
};

/* =========================
   CONSTANTS
========================= */

const MAX_LENGTH = 2000;

const applyNotesUpdate = (
  response: NotesResponse | undefined,
  removedNoteId: string | number | null,
  setNotes: Dispatch<SetStateAction<Notes[]>>,
) => {
  if (Array.isArray(response?.notes)) {
    setNotes(response.notes);
    return;
  }

  if (removedNoteId !== null) {
    setNotes((prev) => prev.filter((n) => n.id !== removedNoteId));
  }
};

/* =========================
   COMPONENT
========================= */

export const OrderNotes = ({ orderNotes }: OrderNotesProps) => {
  const invalidate = useInvalidate();
  const { orderId } = useParams<{ orderId: string }>();
  const selectedCountry = useMarketplaceStore((s) => s.selectedCountry);

  const { translate } = useTranslation("pages.orders.detail.notes");

  const [notes, setNotes] = useState<Notes[]>(orderNotes);
  const [noteInput, setNoteInput] = useState("");

  const sortedNotes = useMemo(
    () =>
      [...notes].sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime(),
      ),
    [notes],
  );

  const { mutate, isLoading } = useCustomMutation<NotesResponse>();

  const handleSendNote = () => {
    if (!noteInput.trim() || !selectedCountry) return;

    mutate(
      {
        url: `store/${selectedCountry.id}/order/${orderId}/notes`,
        method: "post",
        values: { message: noteInput.trim() },
        successNotification: () => ({
          message: translate("sendSuccess"),
          type: "success",
        }),
      },
      {
        onSuccess: ({ data }) => {
          applyNotesUpdate(data, null, setNotes);
          setNoteInput("");
          invalidate({ resource: "all", invalidates: ["all"] });
        },
      },
    );
  };

  const handleDeleteNote = (noteId: string | number) => {
    if (!selectedCountry) return;

    mutate(
      {
        url: `store/${selectedCountry.id}/order/${orderId}/notes/${noteId}`,
        method: "delete",
        values: {},
        successNotification: () => ({
          message: translate("deleteSuccess"),
          type: "success",
        }),
      },
      {
        onSuccess: ({ data }) => {
          applyNotesUpdate(data, noteId, setNotes);
          invalidate({ resource: "all", invalidates: ["all"] });
        },
      },
    );
  };

  return (
    <OrderMessagesCard
      label={translate("notes")}
      badgeCount={sortedNotes.length}
      messages={sortedNotes}
      emptyText={translate("emptySubtitle")}
      inputValue={noteInput}
      onInputChange={setNoteInput}
      onSend={handleSendNote}
      sendDisabled={isLoading || noteInput.length > MAX_LENGTH}
      placeholder={translate("typeNote")}
      onDelete={handleDeleteNote}
    />
  );
};
